import React, { useState, useRef } from 'react';

const Step3 = ({ formData, setFormData, prevStep, submitApplication }) => {
  const [previews, setPreviews] = useState({
    panPhoto: null,
    aadharFront: null,
    aadharBack: null,
    userPhoto: null
  });
  const [uploading, setUploading] = useState(false);
  const [activeCamera, setActiveCamera] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async (field) => {
    setActiveCamera(field);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setActiveCamera(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const photoData = canvas.toDataURL('image/jpeg');
      
      setFormData({ ...formData, [activeCamera]: photoData });
      setPreviews({ ...previews, [activeCamera]: photoData });
      stopCamera();
    }
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, [field]: reader.result });
        setPreviews({ ...previews, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (field) => {
    setFormData({ ...formData, [field]: '' });
    setPreviews({ ...previews, [field]: null });
  };

  const isValid = () => {
    return formData.panNumber && 
           formData.panPhoto && 
           formData.aadharNumber && 
           formData.aadharNumber.length === 12 &&
           formData.aadharFront && 
           formData.aadharBack &&
           formData.userPhoto;
  };

  const handleSubmit = async () => {
    if (isValid()) {
      setUploading(true);
      await submitApplication();
      setUploading(false);
    }
  };

  const renderUploadSection = (label, field, required = true) => (
    <div className="form-group">
      <label>{label} {required && '*'}</label>
      <div className="upload-options">
        <div className="upload-buttons">
          <button 
            type="button" 
            className="upload-btn"
            onClick={() => document.getElementById(`${field}_file`).click()}
          >
            📁 Upload from Device
          </button>
          <button 
            type="button" 
            className="upload-btn camera-btn"
            onClick={() => startCamera(field)}
          >
            📷 Take Photo with Camera
          </button>
        </div>
        <input
          type="file"
          id={`${field}_file`}
          accept="image/*"
          onChange={(e) => handleFileChange(e, field)}
          style={{ display: 'none' }}
        />
      </div>
      {previews[field] && (
        <div className="file-preview">
          <img src={previews[field]} alt={label} />
          <button onClick={() => removePhoto(field)}>Remove</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-number">3</div>
        <div className="step-title">
          <h3>Document Upload</h3>
          <p>Upload required documents for verification</p>
        </div>
      </div>

      <div className="step-form">
        {/* Camera Modal */}
        {activeCamera && (
          <div className="camera-modal" onClick={stopCamera}>
            <div className="camera-content" onClick={(e) => e.stopPropagation()}>
              <h4>Take Photo</h4>
              <video ref={videoRef} autoPlay playsInline className="camera-video"></video>
              <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
              <div className="camera-buttons">
                <button onClick={capturePhoto} className="capture-btn">📸 Capture</button>
                <button onClick={stopCamera} className="close-camera-btn">Close</button>
              </div>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>PAN Card Number *</label>
            <input
              type="text"
              name="panNumber"
              value={formData.panNumber}
              onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
              placeholder="ABCDE1234F"
              maxLength="10"
              pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
              required
            />
            <small>Enter PAN number as per card</small>
          </div>
        </div>

        {renderUploadSection('PAN Card Photo', 'panPhoto')}

        <div className="form-row">
          <div className="form-group">
            <label>Aadhar Number *</label>
            <input
              type="text"
              name="aadharNumber"
              value={formData.aadharNumber}
              onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
              placeholder="1234 5678 9012"
              maxLength="12"
              pattern="[0-9]{12}"
              required
            />
          </div>
        </div>

        <div className="form-row">
          {renderUploadSection('Aadhar Front Photo', 'aadharFront')}
          {renderUploadSection('Aadhar Back Photo', 'aadharBack')}
        </div>

        {renderUploadSection('Your Photo (Selfie)', 'userPhoto')}

        <div className="step-actions dual">
          <button onClick={prevStep} className="btn-prev">
            ← Back
          </button>
          <button onClick={handleSubmit} className="btn-submit" disabled={!isValid() || uploading}>
            {uploading ? 'Submitting...' : 'Submit Application →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step3;